import * as React from 'react';
import {compact} from 'underscore';

import {StarredItemsByObjectId} from 'app-core/platform/starrable/CoreObjectStarIcon';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
  ListingTypes,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  StudyableItemListingExtras,
  maybeGetStudiesListingColumn,
} from 'studies/studyable/StudiesListingColumn';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import * as DateUtil from 'util/DateUtil';

interface AnalyticsListingExtras extends StudyableItemListingExtras {}

export default abstract class BaseAnalyticsListingRenderer<
  T extends ListingRendererType.ANALYSIS | ListingRendererType.ANALYTICS_DASHBOARD,
> extends BaseListingRendererWithPlugins<T> {
  abstract icon: BenchlingIconName;

  protected renderStarIcon(
    _item: ListingTypes[T],
    _starredItemsByObjectId?: StarredItemsByObjectId
  ): React.ReactNode {
    return null;
  }

  getNonPluginColumnConfigurations(
    items: Array<ListingTypes[T]> = [],
    {
      itemStudyAttributes,
      starredItemsByObjectId,
    }: AnalyticsListingExtras & {
      starredItemsByObjectId?: StarredItemsByObjectId;
    } = {}
  ): Array<ColumnConfiguration<ListingTypes[T]>> {
    return compact([
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
        },
        render: (item: ListingTypes[T]) => <div>{item.name}</div>,
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_STARRED),
          accessorKey: ColumnAccessorKey.STAR,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_STARRED,
          width: 70,
        },
        render: (item: ListingTypes[T]) => this.renderStarIcon(item, starredItemsByObjectId),
        isHidden: (item: ListingTypes[T]) => this.renderStarIcon(item, starredItemsByObjectId) == null,
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
          width: 120,
        },
        render: (item: ListingTypes[T]) => (
          <Tooltip
            title={FileBrowserUtil.getResultDate(item, {showTime: true}).date}
            container="body"
            placement={TooltipPlacement.TOP}
            delay={350}
          >
            <span className="u-ellipsis-text">
              {FileBrowserUtil.getResultDate(item, {showTime: false}).date}
            </span>
          </Tooltip>
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          accessorKey: ColumnAccessorKey.CREATED_AT,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
          width: 120,
        },
        render: (item: ListingTypes[T]) => (
          <Tooltip
            title={DateUtil.readableTime(item.createdAt, {showTime: true})}
            container="body"
            placement={TooltipPlacement.TOP}
            delay={350}
          >
            <span className="u-ellipsis-text">
              {DateUtil.readableTime(item.createdAt, {showTime: false})}
            </span>
          </Tooltip>
        ),
      },
      {
        column: {
          header: 'Project',
          accessorKey: ColumnAccessorKey.PROJECT,
          flexGrow: 1,
        },
        render: (item: ListingTypes[T]) => item.project.name,
      },
      {
        column: {
          header: 'Creator',
          accessorKey: ColumnAccessorKey.CREATOR,
          flexGrow: 1,
        },
        render: (item: ListingTypes[T]) => getPartyDisplayString(item.creator),
      },
      maybeGetStudiesListingColumn<ListingTypes[T]>(itemStudyAttributes),
    ]);
  }

  getNonPluginRowContentHeight(): number {
    return TABLE_ROW_BODY_HEIGHT;
  }

  renderIcon(): JSX.Element {
    return <BenchlingIcon className="scrollableTable-cell-icon" icon={this.icon} />;
  }

  openContextMenu(): void {}
}
